if (!customElements.get("video-player")) {
  class VideoPlayer extends HTMLElement {
    constructor() {
      super();
      if (this.hasAttribute("data-init")) {
        this.init();
      }
    }

    init() {
      this.parentSelector = this.dataset.parent;
      this.parent = this.closest(this.parentSelector);

      // 防止重复初始化
      if (this.initialized) return;
      this.initialized = true;

      switch (this.dataset.type) {
        case "youtube":
          this.initYoutubeVideo();
          break;
        case "vimeo":
          this.initVimeoVideo();
          break;
        case "mp4":
          this.initMp4Video();
          break;
      }
    }

    initYoutubeVideo() {
      if (this.querySelector("iframe")) return; // ✅ 已经有 iframe 不再重复

      this.setAsLoading();
      this.loadScript("youtube").then(this.setupYoutubePlayer.bind(this));
    }

    initVimeoVideo() {
      if (this.querySelector("iframe")) return; // ✅ 已经有 iframe 不再重复

      this.setAsLoading();
      this.loadScript("vimeo").then(this.setupVimeoPlayer.bind(this));
    }

    initMp4Video() {
      if (this.querySelector("video")) return; // ✅ 已经有 video 不再重复

      const videoEl = document.createElement("video");
      videoEl.src = this.dataset.videoId;
      videoEl.controls = this.hasAttribute("controls");
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.autoplay = true;
      videoEl.loop = true;
      this.appendChild(videoEl);
    }

    loadScript(type) {
      return new Promise((resolve, reject) => {
        const src =
          type === "youtube"
            ? "//www.youtube.com/iframe_api"
            : "//player.vimeo.com/api/player.js";

        // ✅ 避免重复插入同一个 script
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          if (existingScript.dataset.loaded === "true") {
            resolve();
          } else {
            existingScript.addEventListener("load", () => resolve());
            existingScript.addEventListener("error", reject);
          }
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.dataset.loaded = "false";
        script.onload = () => {
          script.dataset.loaded = "true";
          resolve();
        };
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }

    setAsLoading() {
      this.parent?.setAttribute("loading", true);
    }

    setAsLoaded() {
      this.parent?.removeAttribute("loading");
      this.parent?.setAttribute("loaded", true);
    }

    setupYoutubePlayer() {
      if (this.player) return; // ✅ 已经初始化过就跳过

      const videoId = this.dataset.videoId;
      const interval = setInterval(() => {
        if (window.YT) {
          window.YT.ready(() => {
            clearInterval(interval);

            // ✅ 先清理旧的，避免重复 append
            this.innerHTML = "";

            const container = document.createElement("div");
            this.appendChild(container);

            this.player = new YT.Player(container, {
              videoId: videoId,
              playerVars: {
                showinfo: 0,
                controls: this.hasAttribute("controls") ? 1 : 0,
                fs: 0,
                rel: 0,
                height: "100%",
                width: "100%",
                iv_load_policy: 3,
                html5: 1,
                loop: 1,
                playsinline: 1,
                modestbranding: 1,
                disablekb: 1,
              },
              events: {
                onReady: this.onYoutubeReady.bind(this),
                onStateChange: this.onYoutubeStateChange.bind(this),
              },
            });
          });
        }
      }, 200);
    }

    onYoutubeReady() {
      this.iframe = this.querySelector("iframe");
      this.iframe?.setAttribute("tabindex", "-1");
      if (typeof this.player.playVideo === "function") {
        this.player.playVideo();
        this.player.mute();
      }
      this.setAsLoaded();
    }

    onYoutubeStateChange(event) {
      switch (event.data) {
        case -1:
          if (this.attemptedToPlay) {
            this.setAsLoaded();
          }
          break;
        case 0:
          this.youtubePlay();
          break;
        case 1:
          this.setAsLoaded();
          break;
        case 3:
          this.attemptedToPlay = true;
          break;
      }
    }

    youtubePlay() {
      if (this.player?.playVideo) {
        this.player.playVideo();
      }
    }

    youtubePause() {
      if (this.player?.pauseVideo) {
        this.player.pauseVideo();
      }
    }

    setupVimeoPlayer() {
      if (this.player) return; // ✅ 已经初始化过就跳过

      const videoId = this.dataset.videoId;
      const interval = setInterval(() => {
        if (window.Vimeo) {
          this.player = new Vimeo.Player(this, {
            id: videoId,
            autoplay: true,
            autopause: false,
            background: false,
            controls: this.hasAttribute("controls"),
            loop: true,
            height: "100%",
            width: "100%",
            muted: false,
          });
          this.player.ready().then(this.onVimeoReady.bind(this));
          clearInterval(interval);
        }
      }, 50);
    }

    onVimeoReady() {
      this.iframe = this.querySelector("iframe");
      this.iframe?.setAttribute("tabindex", "-1");
      this.setAsLoaded();
    }

    vimeoPlay() {
      if (this.player?.play) {
        this.player.play();
        this.player.mute();
      }
    }

    vimeoPause() {
      if (this.player?.pause) {
        this.player.pause();
      }
    }

    play() {
      console.log("play");
      const video = this.querySelector("video");
      video?.play();
    }

    pause() {
      console.log("pause");
      this.querySelector("video")?.pause();
    }
  }

  customElements.define("video-player", VideoPlayer);
}
